import React from 'react';
import styles from '../styles/step1WritingModal.module.css';
import type { Step1ArchiveSummary } from '../services/step1WritingService';
import { payloadHasMeaningfulStep1Content, readStep1LocalBackup } from '../services/step1WritingService';

interface Step1RecoverOverlayProps {
  userBackupKey: string;
  archiveSummaries: Step1ArchiveSummary[];
  isArchivesLoading: boolean;
  onClose: () => void;
  onRestoreArchive: (summary: Step1ArchiveSummary) => void;
  onRestoreLocal: () => void;
}

const Step1RecoverOverlay: React.FC<Step1RecoverOverlayProps> = ({
  userBackupKey,
  archiveSummaries,
  isArchivesLoading,
  onClose,
  onRestoreArchive,
  onRestoreLocal,
}) => {
  const local = readStep1LocalBackup(userBackupKey);

  return (
    <div className={styles.recoverOverlay} onClick={onClose}>
      <div
        className={styles.recoverModal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="s1-recover-title"
        aria-modal="true"
      >
        <div className={styles.recoverHeader}>
          <h2 id="s1-recover-title">Earlier saves of your story</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className={styles.recoverBody}>
          <p className={styles.recoverIntro}>
            Recovery Commons keeps earlier saves while you work. If you restore one, your current
            story is kept as another save — nothing is thrown away.
          </p>
          {local && payloadHasMeaningfulStep1Content(local.payload) && (
            <div className={styles.recoverSection}>
              <h3 className={styles.recoverSectionTitle}>Saved on this phone or computer</h3>
              <button type="button" className={styles.btn} onClick={onRestoreLocal}>
                Use save from{' '}
                {new Date(local.savedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </button>
            </div>
          )}
          <div className={styles.recoverSection}>
            <h3 className={styles.recoverSectionTitle}>Earlier saves in Recovery Commons</h3>
            {isArchivesLoading && <p className={styles.recoverLoading}>Loading earlier saves…</p>}
            {!isArchivesLoading && archiveSummaries.length === 0 && (
              <p className={styles.recoverEmpty}>
                No earlier saves are available yet. They are created automatically as you work.
              </p>
            )}
            {!isArchivesLoading && archiveSummaries.length > 0 && (
              <ul className={styles.recoverList}>
                {archiveSummaries.map((summary) => (
                  <li key={summary.version} className={styles.recoverListItem}>
                    <div className={styles.recoverListMeta}>
                      <strong>{summary.label}</strong>
                      <span>{summary.wordCount.toLocaleString()} words</span>
                    </div>
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() => onRestoreArchive(summary)}
                    >
                      Use this save
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className={styles.recoverFooter}>
          <button type="button" className={styles.btn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step1RecoverOverlay;
